import React, { useContext, useEffect, useState } from 'react';
import { Card } from '@uhg-abyss/web/ui/Card';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';

import { useDispatch } from 'react-redux';
import { useGetFileDetailsQuery } from '@src/store/services/cdb/onlineServices';
import { FileContext } from '@src/routes/Routes';
import { LoadingScreen } from '@src/common/LoadingScreen';
import { FilterAndDownload } from './FilterAndDownload';
import { FileDetails } from './FileDetails';
import { setReportTypeVal } from '../../store/ReportTypeSlice';
import { ProcessingStats } from './ProcessingStats';
import { ErrorWarningSummary } from './ErrorWarningSummary';
import { SubmitterCorrections } from './SubmitterCorrections';
import { EligibilityAnalystCorrections } from './EligibilityAnalystCorrections';
import { PCPRollover } from './PCPRollover/PCPRollover';
import { ChangeTransaction } from './ChangeTransaction';
import { AddTransaction } from './AddTransaction';
import { NoChangeTransaction } from './NoChangeTransaction';

import { TermSubmitted } from './TermSubmitted/TermSubmitted';
import { FieldChangeSummaryChild } from './FieldChangeSummary/FieldChangeSummaryChild';

import { FieldChangeSummary } from './FieldChangeSummary';
import { AllTransactions } from './AllTransactions';
import { PCPErrorReport } from './PCPErrorReport/PCPErrorReport';
import { PendingGPSStatusUpdateReport } from './PendingGPSStatusUpdateReport';

export const DEFAULT_REPORT_TYPE = 'procStat';

const RenderReport = ({
  isChild,
  fileId,
  setId,
  reportType,
  fieldCode,
  onChildValueChange,
  errorCode,
  procStatus,
}) => {
  switch (reportType) {
    case 'EE': {
      return <SubmitterCorrections procStatus={procStatus} />;
    }
    case 'IE': {
      return <EligibilityAnalystCorrections procStatus={procStatus} />;
    }
    case 'PCPI': {
      return <PCPErrorReport />;
    }
    case 'ENW': {
      return (
        <ErrorWarningSummary fileId={fileId} isChild={isChild} setId={setId} />
      );
    }
    case 'FC': {
      return (
        <FieldChangeSummaryChild
          fileId={fileId}
          fieldCode={fieldCode}
          isChild={isChild}
          setId={setId}
        />
      );
    }
    case 'FCH': {
      return (
        <FieldChangeSummary fileId={fileId} isChild={isChild} setId={setId} />
      );
    }
    case 'AT': {
      return (
        <AllTransactions
          setId={setId}
          procStatus={procStatus}
          fileId={fileId}
        />
      );
    }
    case 'procStat': {
      return (
        <ProcessingStats
          setId={setId}
          onChildValueChange={onChildValueChange}
        />
      );
    }
    case 'NCH': {
      return (
        <NoChangeTransaction fileId={fileId} isChild={isChild} setId={setId} />
      );
    }
    case 'PROR': {
      return <PCPRollover />;
    }
    case 'CHG': {
      return (
        <ChangeTransaction fileId={fileId} isChild={isChild} setId={setId} />
      );
    }
    case 'ADD': {
      return <AddTransaction />;
    }
    case 'TRM': {
      return <TermSubmitted fileId={fileId} isChild={isChild} setId={setId} />;
    }
    case 'GFB': {
      return <PendingGPSStatusUpdateReport />;
    }

    default: {
      return null;
    }
  }
};

export const Reports = ({ isChild = false }) => {
  const [transSetId, setTransSetId] = useState('');
  const [showReportTable, setShowReportTable] = useState(false);
  const { fileId, setId, fieldCode, reportType, errorCode } =
    useRouter().getRouteParams();
  const {
    data: fileDetailsList,
    isLoading,
    refetch,
  } = useGetFileDetailsQuery(setId);
  const dispatch = useDispatch();
  const { setFileData } = useContext(FileContext);
  const router = useRouter();
  const { service } = router.getRouteParams();

  // Force refetch when component mounts or setId changes
  useEffect(() => {
    refetch();
  }, [setId, refetch]);

  useEffect(() => {
    if (fileDetailsList?.[0]) {
      setShowReportTable(true);
      setFileData((prevData) => {
        return { ...prevData, ...fileDetailsList?.[0] };
      });
      setTransSetId(fileDetailsList[0]?.transSetId);
    }
  }, [fileDetailsList]);

  const handleReportChange = (report) => {
    router.navigate(
      `${service === 'gps' ? '/gps' : ''}/home/${
        isChild ? 'child-reports' : 'reports'
      }/${setId}${isChild ? `/${setId}` : ''}/${report}`
    );

    dispatch(setReportTypeVal(report));
  };

  return (
    <Card css={{ 'abyss-card-root': { padding: '0' } }}>
      <div style={{ padding: '16px' }}>
        <FilterAndDownload
          setId={setId}
          fileId={fileId}
          reportType={reportType}
          fileDetail={fileDetailsList}
          onChange={handleReportChange}
          isChild={isChild}
        />
        <br />
        <FileDetails fileDetail={fileDetailsList} isChild={isChild} />
        <br />
        <br />
        {!showReportTable ? (
          <LoadingScreen />
        ) : (
          <RenderReport
            isChild={isChild}
            fileId={fileId}
            setId={transSetId}
            reportType={reportType}
            fieldCode={fieldCode}
            errorCode={errorCode}
            procStatus={fileDetailsList?.[0]?.fileStatus || ''}
            onChildValueChange={handleReportChange}
          />
        )}
      </div>
    </Card>
  );
};
